import type {CopilotChatMessage} from '@github-ui/copilot-chat-types'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {type CSSProperties, useEffect} from 'react'

import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatScroll} from './ChatScrollContainer'

interface ChatMessagesGroupProps {
  isLastGroup: boolean
  // Optional. Lets a parent set whether the last group fills the screen, instead of this
  // component working it out from state. Immersive chat passes this. See the longer note on
  // `fillViewport` below for why.
  fillViewport?: boolean
  children: React.ReactNode
}

export const ChatMessagesGroup = ({
  isLastGroup,
  fillViewport: fillViewportOverride,
  children,
}: ChatMessagesGroupProps) => {
  const {scrollContainerHeight, scrollToBottom} = useChatScroll()
  const {threadHasNewMessages, pendingFirstMessage} = useChatStateValues('threadHasNewMessages', 'pendingFirstMessage')

  // `fillViewport` makes the last group (a group is one user message plus the assistant replies
  // to it) stretch to fill the screen and scroll down after the user sends, edits, or retries a
  // message. A parent can pass it as a prop, or leave it out and let this component compute it
  // from the state above.
  //
  // This is needed because immersive chat delays rendering its message list (to keep typing and
  // submitting fast). This component's state updates one render before that delayed list, so if
  // this component computed the value itself the OLD last group would briefly stretch and scroll.
  // Passing it as a prop keeps the decision in sync with the list immersive is actually showing.
  //
  // Parents that pass nothing (the PR panel, or immersive with the flag off) fall back to this
  // component's state.
  const fillViewport = fillViewportOverride ?? (isLastGroup && (threadHasNewMessages || pendingFirstMessage))

  useEffect(() => {
    if (fillViewport) scrollToBottom('smooth')
  }, [fillViewport, scrollToBottom])

  let style: CSSProperties | undefined = undefined
  if (fillViewport) {
    if (ssrSafeWindow?.CSS?.supports('container-type: size')) {
      style = {minHeight: '100cqh'}
    } else {
      style = {minHeight: scrollContainerHeight}
    }
  }

  return <div style={style}>{children}</div>
}

type NotEmptyArray<T> = [T, ...T[]]

/** Each group is a set of user requests followed by assistant responses. */
export function groupChatMessages(messages: readonly CopilotChatMessage[]): CopilotChatMessage[][] {
  return messages.reduce<Array<NotEmptyArray<CopilotChatMessage>>>((groups, message) => {
    const lastGroup = groups.at(-1)
    const lastRole = lastGroup?.at(-1)?.role ?? 'assistant'

    // every time the role changes from assistant to user, create a new group
    if (lastRole === 'assistant' && message.role === 'user') groups.push([message])
    else lastGroup?.push(message)

    return groups
  }, [])
}
